import React, { useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
    InvoiceListEntry,
    InvoiceListPage,
    InvoiceListPeriod,
    useLazyListInvoicesQuery,
    useListInvoicesQuery,
} from '../../api/invoice/invoiceApiSlice';
import CustomButton from '../../components/CustomButton';
import PageHeader from '../../components/PageHeader';
import PageHeadline from '../../components/PageHeadline';
import PageSubtitle from '../../components/PageSubtitle';
import TableCard, { tableCellClass, tableHeaderCellClass } from '../../components/TableCard';
import InputField from '../../components/ui/InputField';
import { messageOf } from '../../components/invoice/invoiceMessages';
import { useInvoicePdfDownload } from '../../hooks/useInvoicePdfDownload';
import { getCurrentLang } from '../../i18n/i18n';
import { makeRentalBookingDetailsUrl } from '../../routes';
import { tenantSelector } from '../../selector/authenticationSelector';
import { STATION_TIME_ZONE, stationCalendarDay } from '../../utils/stationTime';

/** The running month up to today, as local (Europe/Berlin) days — the server reads them the same way. */
export const currentMonthPeriod = (now: Date = new Date()): InvoiceListPeriod => {
    const today = stationCalendarDay(now);
    return { from: `${today.slice(0, 8)}01`, to: today };
};

type MorePages = { base: InvoiceListPage | undefined, pages: InvoiceListPage[], loading: boolean, failed: boolean };
const noMore = (base: InvoiceListPage | undefined): MorePages => ({ base, pages: [], loading: false, failed: false });

const isDay = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value);

const StatusLabel = ({ status }: { status: string }) => {
    const { t } = useTranslation();
    const normalized = status.toLowerCase();
    let key = 'account.invoice_status_issued';
    if (normalized === 'canceled') key = 'account.invoice_status_canceled';
    if (normalized === 'corrected') key = 'account.invoice_status_corrected';
    return <span className={'bg-foreground/5 rounded-full px-2 py-0.5 whitespace-nowrap'}>{t(key)}</span>;
};

const InvoiceRow = ({ invoice, locale, onDownload }: {
    invoice: InvoiceListEntry;
    locale: string;
    onDownload: (invoice: InvoiceListEntry) => void;
}) => {
    const { t } = useTranslation();
    const isCancellation = invoice.documentType.toLowerCase() === 'cancellation';
    return (
        <tr className={'border-b border-foreground/5 last:border-b-0'}>
            <td className={`${tableCellClass} font-medium text-foreground whitespace-nowrap`}>{invoice.number}</td>
            <td className={tableCellClass}>
                {t(isCancellation ? 'account.invoice_type_cancellation' : 'account.invoice_type_invoice')}
                {invoice.correctsNumber ? (
                    <div className={'text-xs text-foreground/50'}>
                        {t('invoice_list.corrects', { number: invoice.correctsNumber })}
                    </div>
                ) : null}
            </td>
            <td className={tableCellClass}><StatusLabel status={invoice.status} /></td>
            <td className={`${tableCellClass} whitespace-nowrap`}>
                {new Intl.DateTimeFormat(locale, { timeZone: STATION_TIME_ZONE }).format(new Date(invoice.issuedAt))}
            </td>
            <td className={tableCellClass}>
                {invoice.bookingNumber ? (
                    <Link to={makeRentalBookingDetailsUrl(invoice.orderId)} className={'text-primary hover:underline'}>
                        {invoice.bookingNumber}
                    </Link>
                ) : (
                    <span className={'italic text-foreground/40'}>{t('common.not_applicable')}</span>
                )}
            </td>
            <td className={`${tableCellClass} text-right whitespace-nowrap`}>
                {new Intl.NumberFormat(locale, { style: 'currency', currency: invoice.currency }).format(invoice.totalGross)}
            </td>
            <td className={`${tableCellClass} text-right`}>
                <button type={'button'} className={'text-primary hover:underline'} onClick={() => onDownload(invoice)}>
                    {t('account.invoice_download_short')}
                </button>
            </td>
        </tr>
    );
};

const InvoiceTable = ({ invoices, locale, onDownload, hasMore, more, onLoadMore }: {
    invoices: InvoiceListEntry[];
    locale: string;
    onDownload: (invoice: InvoiceListEntry) => void;
    hasMore: boolean;
    more: MorePages;
    onLoadMore: () => void;
}) => {
    const { t } = useTranslation();
    return (
        <>
            <TableCard>
                <thead>
                    <tr className={'border-b border-foreground/10'}>
                        <th className={tableHeaderCellClass}>{t('account.invoice_issued_number')}</th>
                        <th className={tableHeaderCellClass}>{t('account.invoice_document_type')}</th>
                        <th className={tableHeaderCellClass}>{t('account.invoice_document_status')}</th>
                        <th className={tableHeaderCellClass}>{t('account.invoice_issued_at')}</th>
                        <th className={tableHeaderCellClass}>{t('invoice_list.booking')}</th>
                        <th className={`${tableHeaderCellClass} text-right`}>{t('account.invoice_document_amount')}</th>
                        <th className={tableHeaderCellClass} />
                    </tr>
                </thead>
                <tbody>
                    {invoices.map((invoice) => (
                        <InvoiceRow key={invoice.id} invoice={invoice} locale={locale} onDownload={onDownload} />
                    ))}
                </tbody>
            </TableCard>
            {more.failed ? (
                <p className={'mt-3 text-sm text-foreground/80'}>{t('invoice_list.load_more_failed')}</p>
            ) : null}
            {hasMore ? (
                <div className={'mt-3'}>
                    <CustomButton
                        title={t(more.failed ? 'invoice_list.retry' : 'invoice_list.load_more')}
                        isFilled={false}
                        loading={more.loading}
                        onClick={onLoadMore}
                    />
                </div>
            ) : null}
        </>
    );
};

/**
 * Which state the list is in, each with its own words: an invalid period, a
 * tenant kolula issues nothing for, a failed load (with retry), loading, an
 * empty period, or the table. A failure never reads as an empty list.
 */
const ListBody = ({ periodValid, legacyStore, failure, onRetry, loaded, emptyText, children }: {
    periodValid: boolean;
    legacyStore: boolean;
    failure: string | null;
    onRetry: () => void;
    loaded: boolean;
    emptyText: string;
    children: React.ReactNode;
}) => {
    const { t } = useTranslation();
    if (!periodValid) return <p className={'text-sm text-foreground/60'}>{t('invoice_list.period_invalid')}</p>;
    if (legacyStore) return <p className={'text-sm text-foreground/60'}>{t('invoice_list.not_issuing')}</p>;
    if (failure !== null) {
        return (
            <div className={'flex flex-col gap-3 items-start'}>
                <p className={'text-sm text-foreground/80'}>{failure}</p>
                <CustomButton title={t('invoice_list.retry')} isFilled={false} onClick={onRetry} />
            </div>
        );
    }
    if (!loaded) {
        return (
            <div className={'flex justify-center py-16'}>
                <div className={'animate-spin rounded-full h-10 w-10 border-b-2 border-primary'} />
            </div>
        );
    }
    return children ? <>{children}</> : <p className={'text-sm text-foreground/60'}>{emptyText}</p>;
};

const InvoiceList = () => {
    const { t } = useTranslation();
    const locale = getCurrentLang() === 'en' ? 'en-GB' : 'de-DE';
    const [period, setPeriod] = useState<InvoiceListPeriod>(() => currentMonthPeriod());
    const [downloadFailure, setDownloadFailure] = useState<string | null>(null);
    const download = useInvoicePdfDownload();

    const periodValid = isDay(period.from) && isDay(period.to) && period.from <= period.to;
    const { currentData: first, error, isError, refetch } = useListInvoicesQuery(
        periodValid ? period : skipToken,
        { refetchOnMountOrArgChange: true },
    );

    // Pages after the first, along the server's cursor. They extend exactly
    // the first page they were loaded from (`base`): a new period or a
    // refetched first page starts the list over instead of appending to it.
    const [loadPage] = useLazyListInvoicesQuery();
    const [more, setMore] = useState<MorePages>(noMore(undefined));
    const extra = more.base === first ? more : noMore(first);
    const pages = first ? [first, ...extra.pages] : [];
    const nextCursor = pages.length > 0 ? pages[pages.length - 1].nextCursor : null;

    const invoices = pages.flatMap((p) => p.invoices);
    const issuer = first?.scope.invoiceIssuer?.toLowerCase() ?? null;
    // A legacy store answers 409 `legacy_store`: kolula cannot have issued
    // there. That is a statement about the tenant, not a failed load.
    const legacyStore = (error as { data?: { code?: string } } | undefined)?.data?.code === 'legacy_store';

    const onLoadMore = async () => {
        if (!first || !nextCursor) return;
        const base = first;
        setMore({ ...extra, base, loading: true, failed: false });
        try {
            const next = await loadPage({ ...period, cursor: nextCursor }).unwrap();
            setMore((m) => (m.base === base ? { base, pages: [...m.pages, next], loading: false, failed: false } : m));
        } catch {
            setMore((m) => (m.base === base ? { ...m, loading: false, failed: true } : m));
        }
    };

    const onDownload = async (invoice: InvoiceListEntry) => {
        setDownloadFailure(null);
        try {
            await download(invoice);
        } catch (err: unknown) {
            setDownloadFailure(messageOf(err, t) ?? t('account.invoice_download_failed'));
        }
    };

    const body = (
        <ListBody
            periodValid={periodValid}
            legacyStore={legacyStore}
            failure={isError ? (messageOf(error, t) ?? t('invoice_list.load_failed')) : null}
            onRetry={() => { refetch(); }}
            loaded={first !== undefined}
            emptyText={t(issuer === 'kolula' ? 'invoice_list.empty' : 'invoice_list.not_issuing')}
        >
            {invoices.length > 0 ? (
                <InvoiceTable
                    invoices={invoices}
                    locale={locale}
                    onDownload={onDownload}
                    hasMore={nextCursor !== null}
                    more={extra}
                    onLoadMore={() => { onLoadMore(); }}
                />
            ) : null}
        </ListBody>
    );

    return (
        <div className={'px-4 sm:px-6 py-6 max-w-7xl'}>
            <PageHeader>
                <div className={'flex flex-col grow'}>
                    <PageHeadline>{t('invoice_list.title')}</PageHeadline>
                    {invoices.length > 0 ? (
                        <PageSubtitle>
                            {t(nextCursor ? 'invoice_list.count_partial' : 'invoice_list.count', { count: invoices.length })}
                        </PageSubtitle>
                    ) : null}
                </div>
            </PageHeader>

            <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md mb-4'}>
                <InputField
                    labelAbove
                    type={'date'}
                    label={t('invoice_list.from')}
                    value={period.from}
                    onChange={(value: string) => setPeriod((p) => ({ ...p, from: value }))}
                />
                <InputField
                    labelAbove
                    type={'date'}
                    label={t('invoice_list.to')}
                    value={period.to}
                    onChange={(value: string) => setPeriod((p) => ({ ...p, to: value }))}
                />
            </div>

            {body}
            {downloadFailure ? <p className={'mt-3 text-sm text-foreground/80'}>{downloadFailure}</p> : null}
        </div>
    );
};

/**
 * The tenant's issued invoices and cancellations for a period (#3016), over
 * `GET /admin/invoice` — the invoice index the audit export also reads.
 * Chronological, as the server lists them (a ledger's order, and the order its
 * cursor pages in). Keyed by tenant so period, pages and messages reset on a
 * tenant switch.
 */
const InvoicesPage = () => {
    const tenant = useSelector(tenantSelector);
    return <InvoiceList key={tenant ?? ''} />;
};

export default InvoicesPage;
